import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { getTakeProfitStopLossPrice } from '@ui/utils/trade/getTakeProfitStopLossPrice';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

import { useLiqPrice } from '../../../hooks/useLiqPrice';
import { GenericTradeParams, TpSlValueState } from '../../../types';
import { getTpQuickMaxRatio } from '../../../utils/getTpQuickRatios';
import { validateStopLossPrice } from '../../../utils/validateStoppLossPrice';
import { validateTakeProfitPrice } from '../../../utils/validateTakeProfitPrice';

export const useEditTpValidation = ({
  genericTradeParams,
  tp,
  maxTpRatios,
  spreadPercentage,
  leverage,
}: {
  tp: TpSlValueState;
  maxTpRatios: number | undefined;
  spreadPercentage: number;
  leverage: number;
} & GenericTradeParams) => {
  const pairMarketPrice = usePriceLive(genericTradeParams.pair.baseSymbol);
  const maxTpQuickRatio = getTpQuickMaxRatio(maxTpRatios);

  const maxTpRatiosPrice = useMemo(() => {
    if (
      isNil(pairMarketPrice) ||
      isNil(maxTpQuickRatio) ||
      isNil(leverage) ||
      isNil(genericTradeParams.entryPrice)
    )
      return undefined;

    return getTakeProfitStopLossPrice({
      pnlRate: maxTpQuickRatio,
      isTakeProfit: true,
      entryPrice: genericTradeParams.entryPrice.toNumber(),
      leverage,
      isLong: genericTradeParams.isLong,
      spreadPercentage: spreadPercentage,
    });
  }, [
    genericTradeParams.entryPrice,
    genericTradeParams.isLong,
    leverage,
    maxTpQuickRatio,
    pairMarketPrice,
    spreadPercentage,
  ]);

  if (
    isNil(pairMarketPrice) ||
    isNil(maxTpQuickRatio) ||
    isNil(maxTpRatiosPrice) ||
    isNil(leverage)
  )
    return undefined;

  return validateTakeProfitPrice({
    isLong: genericTradeParams.isLong,
    entryPrice: genericTradeParams.entryPrice.toNumber(),
    tp,
    maxTpRatios: maxTpQuickRatio,
    maxTpRatiosPrice,
    marketPrice: pairMarketPrice.toNumber(),
    isMarket: false,
  });
};

export const useEditSlValidation = ({
  genericTradeParams,
  sl,
}: {
  sl: TpSlValueState;
} & GenericTradeParams) => {
  const liqPrice = useLiqPrice({ genericTradeParams });
  const pairMarketPrice = usePriceLive(genericTradeParams.pair.baseSymbol);

  if (isNil(liqPrice) || isNil(pairMarketPrice)) return undefined;

  return validateStopLossPrice({
    isLong: genericTradeParams.isLong,
    entryPrice: genericTradeParams.entryPrice.toNumber(),
    sl,
    liqPrice,
    marketPrice: pairMarketPrice.toNumber(),
    isMarket: false,
  });
};
